import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

interface FirebaseRulesHelperProps {
  open: boolean;
  onClose: () => void;
}

const FirebaseRulesHelper: React.FC<FirebaseRulesHelperProps> = ({ open, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [copied, setCopied] = useState(false);

  const firebaseRules = `rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    
    // دوال مساعدة للتحقق من الصلاحيات
    function isAuthenticated() {
      return request.auth != null;
    }
    
    function isOwner(userId) {
      return request.auth.uid == userId;
    }
    
    function hasRole(role) {
      return isAuthenticated() &&
             exists(/databases/$(database)/documents/users/$(request.auth.uid)) &&
             get(/databases/$(database)/documents/users/$(request.auth.uid)).data.role == role;
    }
    
    function isAdmin() {
      return hasRole('admin') || hasRole('ADMIN') || hasRole('Administrator');
    }
    
    function isBranchManager() {
      return hasRole('BRANCH_MANAGER') || hasRole('branch_manager') || hasRole('manager');
    }
    
    function isWarehouseManager() {
      return hasRole('WAREHOUSE_MANAGER') || hasRole('warehouse_manager');
    }
    
    function isManager() {
      return isBranchManager() || isWarehouseManager() || isAdmin();
    }
    
    function isEmployee() {
      return hasRole('employee') || hasRole('EMPLOYEE') ||
             hasRole('TECHNICIAN') || hasRole('technician') ||
             hasRole('CASHIER') || hasRole('cashier') ||
             hasRole('SALES') || hasRole('sales') ||
             hasRole('INVENTORY_CLERK') || hasRole('inventory_clerk') ||
             isManager();
    }
    
    function isPublicReadAllowed() {
      return true;
    }
    
    // قواعد عامة لجميع المجموعات
    match /{document=**} {
      allow read, write: if isAuthenticated() && isEmployee();
    }
    
    // قراءة عامة للمتجر الإلكتروني
    match /inventory/{itemId} {
      allow read: if true;
    }
    
    match /categories/{categoryId} {
      allow read: if true;
    }
    
    match /promotions/{promotionId} {
      allow read: if true;
    }
    
    match /pages/{pageId} {
      allow read: if true;
    }
    
    match /deviceTypes/{deviceTypeId} {
      allow read: if true;
    }
    
    match /deviceSizes/{deviceSizeId} {
      allow read: if true;
    }
    
    match /deviceBrands/{deviceBrandId} {
      allow read: if true;
    }
    
    match /deviceAccessories/{deviceAccessoryId} {
      allow read: if true;
    }
  }
}`;

  const handleCopyRules = async () => {
    try {
      await navigator.clipboard.writeText(firebaseRules);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy rules:', error);
    }
  };

  const handleOpenFirebaseConsole = () => {
    window.open('https://console.firebase.google.com/', '_blank');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        🔥 مساعد قواعد Firebase
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              ❌ مشكلة في صلاحيات قاعدة البيانات
            </Typography>
            <Typography>
              Missing or insufficient permissions - يجب تحديث قواعد Firebase
            </Typography>
          </Alert>
        </Box>

        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
          <Tab label="📋 التعليمات" />
          <Tab label="📄 القواعد" />
        </Tabs>

        {tabValue === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              🛠️ خطوات الحل:
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                1️⃣ اذهب إلى Firebase Console:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<OpenIcon />}
                  onClick={handleOpenFirebaseConsole}
                  size="small"
                >
                  فتح Firebase Console
                </Button>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                2️⃣ اختر مشروعك واذهب إلى:
              </Typography>
              <Typography component="div">
                • Firestore Database<br/>
                • Rules (تبويب القواعد)<br/>
                • احذف المحتوى الموجود
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                3️⃣ انسخ القواعد الجديدة:
              </Typography>
              <Button
                variant="outlined"
                startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                onClick={handleCopyRules}
                color={copied ? "success" : "primary"}
                size="small"
              >
                {copied ? "تم النسخ ✅" : "نسخ القواعد"}
              </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                4️⃣ الصق القواعد وانشرها:
              </Typography>
              <Typography component="div">
                • الصق القواعد في محرر Firebase<br/>
                • انقر على زر "Publish"<br/>
                • انتظر حتى يتم النشر بنجاح
              </Typography>
            </Box>

            <Alert severity="success">
              <Typography variant="subtitle2" fontWeight="bold">
                ✅ بعد النشر ستعمل جميع الميزات:
              </Typography>
              <Typography component="div" variant="body2">
                • إضافة طلبات الصيانة<br/>
                • نظام التواصل الجماعي<br/>
                • إدارة المخزون والمبيعات<br/>
                • جميع العمليات الأخرى
              </Typography>
            </Alert>
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                📄 قواعد Firebase الكاملة:
              </Typography>
              <Tooltip title={copied ? "تم النسخ!" : "نسخ القواعد"}>
                <IconButton onClick={handleCopyRules} color={copied ? "success" : "primary"}>
                  {copied ? <CheckIcon /> : <CopyIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', maxHeight: 400, overflow: 'auto' }}>
              <TextField
                multiline
                fullWidth
                value={firebaseRules}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  style: { fontFamily: 'monospace', fontSize: '12px' }
                }}
                minRows={20}
              />
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          إغلاق
        </Button>
        <Button
          variant="contained"
          onClick={handleOpenFirebaseConsole}
          startIcon={<OpenIcon />}
        >
          فتح Firebase Console
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FirebaseRulesHelper;
